import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import toast from "react-hot-toast";

const STATUS_CONFIG = {
  unpaid:  { label: "Unpaid",  color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"       },
  partial: { label: "Partial", color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300" },
  paid:    { label: "Paid",    color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" },
  overdue: { label: "Overdue", color: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"       },
};

const TYPE_CONFIG = {
  operating:  { label: "Operating", icon: "Briefcase",   color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"     },
  bill:       { label: "Bill",      icon: "ReceiptText", color: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300" },
  equipment:  { label: "Equipment", icon: "Monitor",     color: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300"     },
};

const AVATAR_COLORS = [
  "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300",
  "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  "bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300",
];

const ExpenseTable = ({ expenses, onDelete }) => {
  if (!expenses.length) return null;

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border overflow-visible">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              {["Date", "Expense #", "Vendor", "Category", "Type", "Amount (SR)", "Status", "Actions"].map((h) => (
                <th key={h} className="px-5 py-4 text-left text-sm font-medium text-foreground whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {expenses.map((expense, index) => (
              <ExpenseRow key={expense.id} expense={expense} index={index} onDelete={onDelete} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ActionBtn = ({ icon, label, color, onClick }) => (
  <div className="relative group">
    <button onClick={onClick} className={`p-1.5 rounded-lg transition-colors ${color}`}>
      <Icon name={icon} size={13} />
    </button>
    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 border border-border">
      {label}
    </div>
  </div>
);

const ExpenseRow = ({ expense, index, onDelete }) => {
  const navigate             = useNavigate();
  const [showDelete, setShowDelete] = useState(false);

  const statusConfig = STATUS_CONFIG[expense.status] || STATUS_CONFIG.unpaid;
  const typeConfig   = TYPE_CONFIG[expense.expenseType]  || TYPE_CONFIG.operating;
  const initials     = (expense.vendorName || "?").split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  const avatarColor  = AVATAR_COLORS[index % AVATAR_COLORS.length];

  const handlePayment = () => {
    navigate("/purchases/payments", {
      state: {
        prefillExpense: {
          expenseId:     expense.id,
          expenseNumber: expense.expenseNumber,
          vendorName:    expense.vendorName,
          amountDue:     expense.amountDue,
        },
      },
    });
  };

  const handleCreditNote = () => {
    navigate("/purchases/credit-notes/new", {
      state: {
        prefillExpense: {
          expenseId:     expense.id,
          expenseNumber: expense.expenseNumber,
          vendorName:    expense.vendorName,
          amount:        expense.amount,
        },
      },
    });
  };

  const actions = [
    {
      icon: "Edit3",        label: "Edit Expense",
      color: "text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20",
      onClick: () => navigate(`/purchases/expenses/${expense.expenseType}/edit`, { state: { expense } }),
    },
    {
      icon: "Eye",          label: "View Expense",
      color: "text-violet-500 hover:bg-violet-50 dark:hover:bg-violet-900/20",
      onClick: () => toast("View expense — coming soon"),
    },
    {
      icon: "Download",     label: "Download",
      color: "text-teal-500 hover:bg-teal-50 dark:hover:bg-teal-900/20",
      onClick: () => toast("Download — coming soon"),
    },
    {
      icon: "CreditCard",   label: "Record Payment",
      color: "text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20",
      onClick: handlePayment,
    },
    {
      icon: "RotateCcw",    label: "Record Refund",
      color: "text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20",
      onClick: () => toast("Record refund — coming soon"),
    },
    {
      icon: "FileMinus",    label: "Create Credit Note",
      color: "text-pink-500 hover:bg-pink-50 dark:hover:bg-pink-900/20",
      onClick: handleCreditNote,
    },
    {
      icon: "Trash2",       label: "Delete",
      color: "text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20",
      onClick: () => setShowDelete(true),
    },
  ];

  return (
    <>
      <motion.tr
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: index * 0.04 }}
        className="hover:bg-muted/50 transition-colors"
      >
        {/* Date */}
        <td className="px-5 py-4 text-sm text-foreground whitespace-nowrap">
          {expense.date
            ? new Date(expense.date).toLocaleDateString("en-SA", { year: "numeric", month: "short", day: "numeric" })
            : "—"}
        </td>

        {/* Expense # */}
        <td className="px-5 py-4">
          <span className="font-mono text-sm text-primary font-semibold">{expense.expenseNumber || "—"}</span>
        </td>

        {/* Vendor */}
        <td className="px-5 py-4">
          <div className="flex items-center gap-2.5">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${avatarColor}`}>
              {initials}
            </div>
            <span className="text-sm font-medium text-foreground">{expense.vendorName || "—"}</span>
          </div>
        </td>

        {/* Category */}
        <td className="px-5 py-4 text-sm text-muted-foreground">{expense.category || "—"}</td>

        {/* Type */}
        <td className="px-5 py-4">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${typeConfig.color}`}>
            <Icon name={typeConfig.icon} size={11} />
            {typeConfig.label}
          </span>
        </td>

        {/* Amount */}
        <td className="px-5 py-4">
          <div>
            <div className="font-semibold text-foreground">
              {parseFloat(expense.amount || 0).toLocaleString("en-SA", { minimumFractionDigits: 2 })}
            </div>
            {parseFloat(expense.amountDue) > 0 && (
              <div className="text-xs text-red-500 mt-0.5">
                Due: SR {parseFloat(expense.amountDue).toLocaleString("en-SA", { minimumFractionDigits: 2 })}
              </div>
            )}
          </div>
        </td>

        {/* Status */}
        <td className="px-5 py-4">
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusConfig.color}`}>
            {statusConfig.label}
          </span>
        </td>

        {/* Actions */}
        <td className="px-5 py-4">
          <div className="flex items-center gap-0.5">
            {actions.map((a) => (
              <ActionBtn key={a.label} icon={a.icon} label={a.label} color={a.color} onClick={a.onClick} />
            ))}
          </div>
        </td>
      </motion.tr>

      <AnimatePresence>
        {showDelete && (
          <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <td colSpan={8} className="px-5 py-3 bg-red-50 dark:bg-red-900/10 border-b border-red-200 dark:border-red-800">
              <div className="flex items-center justify-between">
                <span className="text-sm text-red-700 dark:text-red-300 font-medium">
                  Delete expense <strong>{expense.expenseNumber}</strong> for <strong>{expense.vendorName}</strong>? This cannot be undone.
                </span>
                <div className="flex gap-2 ml-4 flex-shrink-0">
                  <button onClick={() => setShowDelete(false)}
                    className="px-3 py-1.5 text-sm rounded-lg border border-border text-foreground hover:bg-muted transition-colors">
                    Cancel
                  </button>
                  <button onClick={() => { onDelete(expense.id); setShowDelete(false); }}
                    className="px-3 py-1.5 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors">
                    Delete
                  </button>
                </div>
              </div>
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </>
  );
};

export default ExpenseTable;